/**
 * PersonaEditorModal — create or edit a ConversationPersona.
 *
 * Follows §4.5.5 of the Conversations design exactly.
 * Five collapsible sections:
 *   1. Qui est-il ? (name, situation, decisionCriteria, tone, age, gender, budget)
 *   2. Que cherche-t-il ? (intent × 3, startingPrompt, mode, scriptedQuestions)
 *   3. Qu'est-ce qu'on mesure ? (read-only Cibles recap + competitors)
 *   4. Quand s'arrête-t-il ? (tool card, maxTurns)
 *   5. Où, quand, combien ? (zone × engine matrix, cadence)
 *
 * Deviations from the mock (§4.6):
 *   - No target picker (D41/D32 — reads Cibles read-only)
 *   - Turn options stop at 6 (D6)
 *   - Claude removed from engine matrix (D2)
 *   - No zone registry (D32 — uses shipped autocomplete)
 *   - Mode + scripted questions ship (D40)
 */

import { Fragment, useState, useCallback, useMemo, useEffect, type ReactNode } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  IconButton,
  InputLabel,
  LinearProgress,
  Link,
  ListItemIcon,
  ListItemText,
  ListSubheader,
  Menu,
  MenuItem,
  Select,
  Skeleton,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import CloseIcon from '@mui/icons-material/Close';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import RecordVoiceOverOutlinedIcon from '@mui/icons-material/RecordVoiceOverOutlined';
import CakeOutlinedIcon from '@mui/icons-material/CakeOutlined';
import FaceIcon from '@mui/icons-material/Face';
import PaymentsIcon from '@mui/icons-material/Payments';
import CenterFocusStrongIcon from '@mui/icons-material/CenterFocusStrong';
import SchoolIcon from '@mui/icons-material/School';
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';
import PsychologyOutlinedIcon from '@mui/icons-material/PsychologyOutlined';
import { useTranslation } from 'react-i18next';
import { api, ApiError } from '../../services/api';
import {
  CONVERSATION_INTENTS,
  CONVERSATION_MAX_CELLS,
  CONVERSATION_MAX_MESSAGE_CHARS,
  CONVERSATION_MAX_PERSONA_BUDGET_CHARS,
  CONVERSATION_MAX_PERSONA_DESCRIPTION_CHARS,
  CONVERSATION_MAX_PERSONA_LIST_ITEMS,
  CONVERSATION_MAX_PERSONA_NAME_CHARS,
  CONVERSATION_MAX_RUNS,
  CONVERSATION_MAX_TAGS,
  CREDIT_COST_CONVERSATION_TURN,
  CONVERSATION_STOP_CRITERIA,
  TARGET_CATEGORIES,
  type ConversationPersona,
  type ConversationIntentId,
  type ConversationStopCriterionId,
  type ConversationMode,
  type ConversationEngine,
  type ConversationTone,
  type ConversationSelectionFocus,
  type ConversationDomainExpertise,
  type ConversationLanguageStyle,
  type ConversationReceptiveness,
  type TargetsManifest,
  zoneKeyFrom,
} from '@traaker/shared';
import { catCount, CATEGORIES } from '../../pages/targets/CATEGORIES';
import { ZoneAutocomplete, type GeoSuggestion } from './ZoneAutocomplete';
import { hasLaunchToday } from './supersede';

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

/**
 * Accordion step ids, in display order. Section 3 is NOT here — « Qu'est-ce qu'on
 * mesure ? » became the always-visible scope strip (2026-10-03) — but the remaining
 * ids keep their original numbers so every `editorSec<N>*` key and every
 * `openSections`/`doneSections` entry keeps its meaning. Only the number SHOWN to
 * the user is positional, and `advanceSection` steps along this list.
 */
const SECTION_IDS = [1, 2, 4, 5];

// Mirror of the shared CONVERSATION_ENGINES (D2) — keep in lock-step.
const CONVERSATION_ENGINES: ConversationEngine[] = [
  'chatgpt', 'perplexity', 'gemini', 'google', 'mistral',
];
const ENGINE_LABELS: Record<ConversationEngine, string> = {
  chatgpt: 'ChatGPT',
  perplexity: 'Perplexity',
  gemini: 'Gemini',
  // User-facing name only, and deliberately opaque about the transport (product
  // decision 2026-09-28): to the user this is the Google interface, simulated. Keep
  // it in sync with ENGINE_META.google in EngineLogo.tsx.
  google: 'Google AI Overview',
  mistral: 'Mistral',
};

const TONE_KEYS: ConversationTone[] = [
  'neutral', 'direct', 'skeptical', 'enthusiastic', 'technical', 'novice',
];

// ---------------------------------------------------------------------------
// Optional traits (section 1)
// ---------------------------------------------------------------------------

/**
 * The OPTIONAL half of « Qui est-il ? » (2026-10-03).
 *
 * Only name / description / decisionCriteria are always visible — the eight below
 * are added one at a time from a picker, so an untouched card shows three fields
 * instead of eleven. Shape mirrors `pages/targets/CATEGORIES.ts` (module-scope
 * registry of `{ key, Icon, labelKey, descKey }`), which is the house pattern for
 * "a list the UI iterates and a Menu renders".
 *
 * Icons are MUI SVG, not emoji: emoji cannot inherit `currentColor` (so they ignore
 * the disabled and dark states), render differently per OS, and are announced by
 * screen readers under their CLDR name ("birthday cake"). The `SEGMENT_AVATARS`
 * emoji in shared are CONTENT a user picks; these are chrome.
 */
type TraitKey =
  | 'tone' | 'ageRange' | 'gender' | 'budget'
  | 'selectionFocus' | 'domainExpertise' | 'languageStyle' | 'receptiveness';

interface TraitConfig {
  key: TraitKey;
  group: 'profile' | 'behaviour';
  Icon: typeof RecordVoiceOverOutlinedIcon;
  labelKey: string;
  /**
   * One line shown in the picker, BEFORE the user commits to the trait. The four
   * behavioural traits reuse their existing `editor*Tooltip` strings, which already
   * explain what they do — one source of help text, no second copy to drift.
   */
  descKey: string;
}

const TRAITS: TraitConfig[] = [
  { key: 'tone',            group: 'profile',   Icon: RecordVoiceOverOutlinedIcon, labelKey: 'conversations.editorToneLabel',            descKey: 'conversations.editorTraitDescTone' },
  { key: 'ageRange',        group: 'profile',   Icon: CakeOutlinedIcon,            labelKey: 'conversations.editorAgeLabel',             descKey: 'conversations.editorTraitDescAge' },
  { key: 'gender',          group: 'profile',   Icon: FaceIcon,                    labelKey: 'conversations.editorGenderLabel',          descKey: 'conversations.editorTraitDescGender' },
  { key: 'budget',          group: 'profile',   Icon: PaymentsIcon,                labelKey: 'conversations.editorBudgetLabel',          descKey: 'conversations.editorTraitDescBudget' },
  { key: 'selectionFocus',  group: 'behaviour', Icon: CenterFocusStrongIcon,       labelKey: 'conversations.editorSelectionFocusLabel',  descKey: 'conversations.editorSelectionFocusTooltip' },
  { key: 'domainExpertise', group: 'behaviour', Icon: SchoolIcon,                  labelKey: 'conversations.editorDomainExpertiseLabel', descKey: 'conversations.editorDomainExpertiseTooltip' },
  // ChatBubble, deliberately NOT Translate — the modal has a real language axis
  // (section 5's per-cell language) and the two must not read as the same control.
  { key: 'languageStyle',   group: 'behaviour', Icon: ChatBubbleOutlineIcon,       labelKey: 'conversations.editorLanguageStyleLabel',   descKey: 'conversations.editorLanguageStyleTooltip' },
  { key: 'receptiveness',   group: 'behaviour', Icon: PsychologyOutlinedIcon,      labelKey: 'conversations.editorReceptivenessLabel',   descKey: 'conversations.editorReceptivenessTooltip' },
];

/**
 * Which traits an existing persona already carries, so edit mode opens showing
 * exactly the fields that are actually shaping its conversations.
 *
 * `tone` is special: the shared type requires it, so "absent" can only be encoded
 * as the default `'neutral'`. Anything else means the user chose it deliberately.
 */
function traitsFrom(persona?: ConversationPersona): Set<TraitKey> {
  const set = new Set<TraitKey>();
  if (!persona) return set;
  if (persona.tone && persona.tone !== 'neutral') set.add('tone');
  for (const k of ['ageRange', 'gender', 'budget', 'selectionFocus', 'domainExpertise', 'languageStyle', 'receptiveness'] as const) {
    if (persona[k]) set.add(k);
  }
  return set;
}

const CADENCES = ['none', 'daily', 'weekly', 'monthly'] as const;
type CadenceKey = 'none' | 'daily' | 'weekly' | 'monthly';

const CADENCE_MULTIPLIER: Record<CadenceKey, number> = {
  none: 0,
  daily: 30,
  weekly: 4,
  monthly: 1,
};

/** Convert editor's simple cadence key to the shared Cadence type (null = manual only). */
function cadenceToShared(key: CadenceKey): { interval: number; unit: 'days' | 'weeks' | 'months' } | null {
  if (key === 'none') return null;
  if (key === 'daily') return { interval: 1, unit: 'days' };
  if (key === 'weekly') return { interval: 1, unit: 'weeks' };
  return { interval: 1, unit: 'months' };
}

/** Convert a shared Cadence back to editor key (for initial state in edit mode). */
function cadenceFromShared(c: unknown): CadenceKey {
  if (!c || c === 'off') return 'none';
  const { unit } = c as { unit: string };
  if (unit === 'weeks') return 'weekly';
  if (unit === 'months') return 'monthly';
  return 'daily';
}

/** Flag emoji from ISO 3166-1 alpha-2 country code. */
function flagEmoji(cc: string): string {
  return [...cc.toUpperCase()].map(c => String.fromCodePoint(0x1F1E6 + c.charCodeAt(0) - 65)).join('');
}

/** GeoSuggestion type → French label (matching Targets style). */
const ZONE_TYPE_FR: Record<string, string> = {
  country: 'pays', city: 'ville', region: 'région', county: 'département',
  district: 'district', suburb: 'quartier', street: 'rue',
};

/** IANA timezone options grouped by region. */
const TIMEZONE_OPTIONS: { value: string; label: string }[] = [
  { value: 'Europe/Paris', label: 'Europe/Paris (CET)' },
  { value: 'Europe/Brussels', label: 'Europe/Brussels (CET)' },
  { value: 'Europe/London', label: 'Europe/London (GMT)' },
  { value: 'Europe/Berlin', label: 'Europe/Berlin (CET)' },
  { value: 'Europe/Amsterdam', label: 'Europe/Amsterdam (CET)' },
  { value: 'Europe/Madrid', label: 'Europe/Madrid (CET)' },
  { value: 'Europe/Rome', label: 'Europe/Rome (CET)' },
  { value: 'Europe/Zurich', label: 'Europe/Zurich (CET)' },
  { value: 'America/New_York', label: 'America/New_York (ET)' },
  { value: 'America/Chicago', label: 'America/Chicago (CT)' },
  { value: 'America/Los_Angeles', label: 'America/Los_Angeles (PT)' },
  { value: 'America/Montreal', label: 'America/Montreal (ET)' },
  { value: 'America/Sao_Paulo', label: 'America/Sao_Paulo (BRT)' },
  { value: 'Asia/Tokyo', label: 'Asia/Tokyo (JST)' },
  { value: 'Asia/Shanghai', label: 'Asia/Shanghai (CST)' },
  { value: 'Asia/Singapore', label: 'Asia/Singapore (SGT)' },
  { value: 'Australia/Sydney', label: 'Australia/Sydney (AEST)' },
  { value: 'UTC', label: 'UTC' },
];

const LANG_OPTIONS: { value: string; label: string; flag: string }[] = [
  { value: 'fr', label: 'français',    flag: '🇫🇷' },
  { value: 'nl', label: 'néerlandais', flag: '🇳🇱' },
  { value: 'en', label: 'anglais',     flag: '🇬🇧' },
  { value: 'de', label: 'allemand',    flag: '🇩🇪' },
  { value: 'es', label: 'espagnol',    flag: '🇪🇸' },
  { value: 'it', label: 'italien',     flag: '🇮🇹' },
  { value: 'pt', label: 'portugais',   flag: '🇵🇹' },
];

// ---------------------------------------------------------------------------
// Draft cell state (UI only — converted to ConversationCell on submit)
// ---------------------------------------------------------------------------

interface DraftCell {
  /** UI key for React reconciliation. */
  uid: string;
  zone: string;          // zoneKeyFrom result
  zoneSuggestion: GeoSuggestion;
  language: string;
  /** runs per engine: 0 means not configured. */
  engineRuns: Record<ConversationEngine, number>;
}

interface ScriptedQuestion {
  turn: number;
  text: string;
}

interface EditorState {
  name: string;
  description: string;
  /** §7.8 — user-defined tags, ≤ CONVERSATION_MAX_TAGS, lowercase-kebab. */
  tags: string[];
  tone: ConversationTone;
  selectionFocus: '' | ConversationSelectionFocus;
  domainExpertise: '' | ConversationDomainExpertise;
  languageStyle: '' | ConversationLanguageStyle;
  receptiveness: '' | ConversationReceptiveness;
  ageRange: string;
  gender: '' | 'female' | 'male' | 'unspecified';
  budget: string;
  decisionCriteria: string;
  /**
   * Which OPTIONAL traits the user has added to section 1 (2026-10-03). Only
   * name / description / decisionCriteria are always visible; everything else is
   * added from the « Ajouter un trait » picker. A trait not in this set is not
   * merely hidden — `buildPayload` sends `null` for it, which the API deletes, so
   * what the card shows is what shapes the conversation.
   */
  traits: Set<TraitKey>;
  intentId: ConversationIntentId;
  comparatorTargetIds: string[];
  mode: ConversationMode;
  startingPrompt: string;
  scriptedQuestions: ScriptedQuestion[];
  stopCriterion: '' | ConversationStopCriterionId;
  maxTurns: number;
  cadence: CadenceKey;
  timezone: string;
  hour: string;
  cells: DraftCell[];
  // UI section state
  openSections: Set<number>;
  doneSections: Set<number>;
}

function initialState(persona?: ConversationPersona): EditorState {
  if (persona) {
    // Edit mode — map existing cells back to draft form.
    // Group API cells by zone+language — one DraftCell per (zone × language) row,
    // with engineRuns holding the per-engine counts. The API stores one record per
    // (zone × language × engine), so editing back into this grouped form prevents
    // the editor from showing duplicate zone rows when multiple engines are configured.
    const cellsByKey = new Map<string, DraftCell>();
    for (const c of persona.cells) {
      const key = `${c.zone}|${c.language}`;
      if (!cellsByKey.has(key)) {
        cellsByKey.set(key, {
          uid: Math.random().toString(36).slice(2),
          zone: c.zone,
          zoneSuggestion: c.zoneMeta as unknown as GeoSuggestion,
          language: c.language,
          engineRuns: Object.fromEntries(
            CONVERSATION_ENGINES.map((e) => [e, 0]),
          ) as Record<ConversationEngine, number>,
        });
      }
      cellsByKey.get(key)!.engineRuns[c.engine as ConversationEngine] = c.runs;
    }
    const cells = [...cellsByKey.values()];
    return {
      name: persona.name,
      description: persona.description ?? '',
      tags: persona.tags ?? [],
      tone: persona.tone,
      selectionFocus: (persona.selectionFocus ?? '') as EditorState['selectionFocus'],
      domainExpertise: (persona.domainExpertise ?? '') as EditorState['domainExpertise'],
      languageStyle: (persona.languageStyle ?? '') as EditorState['languageStyle'],
      receptiveness: (persona.receptiveness ?? '') as EditorState['receptiveness'],
      ageRange: persona.ageRange ?? '',
      gender: (persona.gender ?? '') as '' | 'female' | 'male' | 'unspecified',
      budget: persona.budget ?? '',
      decisionCriteria: persona.decisionCriteria?.join(', ') ?? '',
      traits: traitsFrom(persona),
      intentId: persona.intentId,
      comparatorTargetIds: (persona as any).comparatorTargetIds ?? [],
      mode: persona.mode,
      startingPrompt: persona.startingPrompt ?? '',
      scriptedQuestions: persona.scriptedQuestions ?? [],
      stopCriterion: persona.stopCriterion ?? '',
      maxTurns: persona.maxTurns,
      cadence: cadenceFromShared(persona.cadence),
      timezone: persona.timezone ?? 'Europe/Paris',
      hour: persona.scheduleHour ?? '06:00',
      cells,
      openSections: new Set([1]),
      doneSections: new Set(),
    };
  }
  return {
    name: '',
    description: '',
    tags: [],
    tone: 'neutral',
    selectionFocus: '',
    domainExpertise: '',
    languageStyle: '',
    receptiveness: '',
    ageRange: '',
    gender: '',
    budget: '',
    decisionCriteria: '',
    traits: new Set(),
    intentId: 'discover',
    comparatorTargetIds: [],
    mode: 'improvised',
    startingPrompt: '',
    scriptedQuestions: [],
    stopCriterion: '',
    maxTurns: CONVERSATION_INTENTS.discover.defaultMaxTurns,
    cadence: 'daily',
    timezone: 'Europe/Paris',
    hour: '06:00',
    cells: [],
    openSections: new Set([1]),
    doneSections: new Set(),
  };
}

// ---------------------------------------------------------------------------
// Cost calculation
// ---------------------------------------------------------------------------

function calcCost(state: EditorState) {
  const totalRuns = state.cells.reduce(
    (acc, c) => acc + CONVERSATION_ENGINES.reduce((s, e) => s + c.engineRuns[e], 0),
    0,
  );
  const totalConfigs = state.cells.reduce(
    (acc, c) => acc + CONVERSATION_ENGINES.filter((e) => c.engineRuns[e] > 0).length,
    0,
  );
  const turnsCount =
    state.mode === 'scripted' && state.scriptedQuestions.length > 0
      ? state.scriptedQuestions.length
      : state.maxTurns;
  const creditsPerLaunch = totalRuns * turnsCount * CREDIT_COST_CONVERSATION_TURN;
  const creditsPerMonth = creditsPerLaunch * CADENCE_MULTIPLIER[state.cadence];
  return {
    totalRuns,
    totalConfigs,
    turnsCount,
    creditsPerLaunch,
    creditsPerMonth,
    isScripted: state.mode === 'scripted',
    overCapRuns: totalRuns > CONVERSATION_MAX_RUNS,
    overCapCells: totalConfigs > CONVERSATION_MAX_CELLS,
  };
}

// ---------------------------------------------------------------------------
// Build the API payload
// ---------------------------------------------------------------------------

function buildPayload(state: EditorState) {
  // Cells: one per (zone, language, engine) combination where runs > 0.
  const cells = state.cells.flatMap((dc) =>
    CONVERSATION_ENGINES.filter((e) => dc.engineRuns[e] > 0).map((e) => ({
      engine: e,
      language: dc.language,
      zone: dc.zone,
      zoneMeta: {
        type: dc.zoneSuggestion.type,
        label: dc.zoneSuggestion.label,
        countryCode: dc.zoneSuggestion.countryCode,
        cityName: dc.zoneSuggestion.cityName,
        regionName: (dc.zoneSuggestion as any).regionName,
        latitude: (dc.zoneSuggestion as any).latitude,
        longitude: (dc.zoneSuggestion as any).longitude,
      },
      runs: dc.engineRuns[e],
    })),
  );

  const payload: Record<string, unknown> = {
    name: state.name.trim(),
    description: state.description.trim(),
    // `tone` is required by the shared type, so a removed tone trait is the default.
    tone: state.traits.has('tone') ? state.tone : 'neutral',
    intentId: state.intentId,
    brandAwareness: 'unaware',
    tags: state.tags,
    mode: state.mode,
    maxTurns: state.maxTurns,
    cadence: cadenceToShared(state.cadence),
    ...(state.cadence !== 'none' && {
      timezone: state.timezone,
      scheduleHour: state.hour,
    }),
    cells,
  };

  // Every optional trait is sent on EVERY save: its value when the trait is on, an
  // explicit `null` when it is off. Omitting it would make PATCH's
  // `body.X !== undefined` test skip the field and leave the stored value in place —
  // the user would see the trait gone from the card while it kept shaping the
  // persona's prompt. `null` is what the API turns into a delete (`clearable`).
  const trait = <T,>(key: TraitKey, value: T) => (state.traits.has(key) && value ? value : null);
  payload.ageRange = trait('ageRange', state.ageRange);
  payload.gender = trait('gender', state.gender);
  payload.budget = trait('budget', state.budget.trim());
  payload.selectionFocus = trait('selectionFocus', state.selectionFocus);
  payload.domainExpertise = trait('domainExpertise', state.domainExpertise);
  payload.languageStyle = trait('languageStyle', state.languageStyle);
  payload.receptiveness = trait('receptiveness', state.receptiveness);

  // Scripted personas stop at the last question, so a stop criterion would be an
  // unused field on a saved persona — which reads as a bug.
  payload.stopCriterion = state.mode === 'scripted' ? null : (state.stopCriterion || null);

  if (state.decisionCriteria.trim()) {
    const parts = state.decisionCriteria
      .split(/[\n,]+/)
      .map((s) => s.trim())
      .filter(Boolean);
    payload.decisionCriteria = parts.length > 0 ? parts : [state.decisionCriteria.trim()];
  }

  // Sent on EVERY save (like `tags`) so PATCH can clear it: `[]` is the delete. The
  // API rejects a non-empty list on a non-compare persona, so switching intent drops it.
  payload.comparatorTargetIds = state.intentId === 'compare' ? state.comparatorTargetIds : [];

  if (state.startingPrompt.trim()) {
    payload.startingPrompt = state.startingPrompt.trim();
  }

  if (state.mode !== 'improvised' && state.scriptedQuestions.length > 0) {
    payload.scriptedQuestions = state.scriptedQuestions;
  }

  return payload;
}

// ---------------------------------------------------------------------------
// Section header component
// ---------------------------------------------------------------------------

interface SectionHeaderProps {
  num: number;
  title: string;
  summary: string;
  open: boolean;
  done: boolean;
  onToggle: () => void;
}

function SectionHeader({ num, title, summary, open, done, onToggle }: SectionHeaderProps) {
  return (
    <Box
      component="button"
      type="button"
      onClick={onToggle}
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1.5,
        px: 2.5,
        py: 2,
        width: '100%',
        background: 'transparent',
        border: 'none',
        cursor: 'pointer',
        textAlign: 'left',
        color: 'text.primary',
        '&:hover': { bgcolor: 'action.hover' },
      }}
    >
      <Box
        sx={{
          width: 28,
          height: 28,
          borderRadius: '50%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontWeight: 700,
          fontSize: '0.75rem',
          flexShrink: 0,
          bgcolor: done && !open ? 'success.light' : open ? 'primary.main' : 'action.selected',
          color: done && !open ? 'success.dark' : open ? 'primary.contrastText' : 'text.secondary',
        }}
      >
        {done && !open ? <CheckCircleIcon sx={{ fontSize: 16 }} /> : num}
      </Box>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography fontWeight={600} fontSize="0.9375rem">{title}</Typography>
        <Typography variant="caption" color="text.secondary" noWrap sx={{ display: 'block' }}>
          {open ? summary : summary}
        </Typography>
      </Box>
      {open ? <ExpandLessIcon sx={{ color: 'text.secondary' }} /> : <ExpandMoreIcon sx={{ color: 'text.secondary' }} />}
    </Box>
  );
}

// ---------------------------------------------------------------------------
// Preview panel
// ---------------------------------------------------------------------------

/**
 * Right column (2026-10-03): identity recap, then whatever the modal slots in as
 * `children` (the tag card and the « On mesure » strip), then the cost line.
 *
 * It used to carry three more cards — a turn-1 message mock-up, a « Ce qui sera
 * mesuré » outcome recap, and an « Effet sur le portefeuille » counter. All three
 * were dropped: the mock-up invented a sentence the engine never sees when
 * `startingPrompt` is empty, and the other two restated what the intent picker and
 * the personas list already say. The column is now metadata, not a simulation.
 */
interface PreviewPanelProps {
  state: EditorState;
  children?: ReactNode;
}

function PreviewPanel({ state, children }: PreviewPanelProps) {
  const { t } = useTranslation();
  const intent = CONVERSATION_INTENTS[state.intentId];
  const cost = calcCost(state);

  return (
    <Stack spacing={1.5}>
      {/* Persona card */}
      <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, p: 2 }}>
        <Typography variant="overline" color="text.secondary" sx={{ fontSize: '0.6875rem' }}>
          {t('conversations.editorPreviewEyebrow')}
        </Typography>
        <Typography fontWeight={600} fontSize="0.9375rem" sx={{ mt: 0.5 }}>
          {state.name || 'Sans nom'}
        </Typography>
        <Chip
          label={intent.name}
          size="small"
          sx={{ mt: 0.75, fontSize: '0.6875rem', fontWeight: 600 }}
          color="primary"
          variant="outlined"
        />
        {state.decisionCriteria && (
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
            {t('conversations.editorDecidesSur')} {state.decisionCriteria.slice(0, 80)}
          </Typography>
        )}
      </Box>

      {children}

      {/* Cost summary */}
      {cost.totalRuns > 0 && (
        <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, p: 2 }}>
          <Typography variant="caption" color={cost.overCapRuns ? 'error' : 'text.secondary'}>
            {cost.isScripted
              ? t('conversations.editorCostBarScripted', { runs: cost.totalRuns, turns: cost.turnsCount, credits: cost.creditsPerLaunch.toLocaleString(), monthly: cost.creditsPerMonth.toLocaleString() })
              : t('conversations.editorCostBar', { runs: cost.totalRuns, turns: cost.turnsCount, credits: cost.creditsPerLaunch.toLocaleString(), monthly: cost.creditsPerMonth.toLocaleString() })}
          </Typography>
        </Box>
      )}
    </Stack>
  );
}

// ---------------------------------------------------------------------------
// Main modal
// ---------------------------------------------------------------------------

interface PersonaEditorModalProps {
  open: boolean;
  onClose: () => void;
  /** If provided, edit mode. Otherwise create mode. */
  persona?: ConversationPersona;
  brandId: string;
  onSuccess: (persona: ConversationPersona) => void;
}

export function PersonaEditorModal({
  open,
  onClose,
  persona,
  brandId,
  onSuccess,
}: PersonaEditorModalProps) {
  const { t } = useTranslation();
  const isEdit = !!persona;

  const [state, setState] = useState<EditorState>(() => initialState(persona));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  /** Anchor for the « Ajouter un trait » picker (section 1). */
  const [traitMenuAnchor, setTraitMenuAnchor] = useState<HTMLElement | null>(null);

  /**
   * Did the last save fail on the no-brand rule? Derived from the API's own 400 rather
   * than re-implemented client-side: `buildForbiddenTokens` folds aliases and
   * apostrophes, and a second local copy of that logic would disagree with the server
   * on exactly the edge cases it exists to catch.
   */
  const briefRuleViolated = !!error && /ne doit pas nommer la marque/.test(error);

  // §7.8 — tag input: the text typed before Enter/comma commits it as a chip.
  const [tagInput, setTagInput] = useState('');

  // Section 3 « Qu'est-ce qu'on mesure ? » — the brand's real Cibles referential.
  // `null` = not loaded yet (render nothing rather than a false « aucune cible »).
  const [targetsManifest, setTargetsManifest] = useState<TargetsManifest | null>(null);

  // Section 5 — établissement zone suggestions (one-click add, no search needed).
  // One suggestion per unique zone key; ordered as they arrive from the API.
  const [etabZones, setEtabZones] = useState<GeoSuggestion[]>([]);

  // Marque targets for the comparaison intent multi-select.
  const [marqueTargets, setMarqueTargets] = useState<{ targetId: string; name: string }[]>([]);

  useEffect(() => {
    if (!open || !brandId) return;
    let cancelled = false;
    api.get<TargetsManifest>(`/brands/${brandId}/targets/summary`)
      .then((m) => { if (!cancelled) setTargetsManifest(m); })
      .catch(() => { /* leave null: no alert, no counts */ });

    // The scope strip's CTA opens Cibles in a NEW TAB (this dialog is local state with
    // no draft path — navigating this tab would destroy the form), so the user comes
    // back by refocusing this one. Without this refetch the strip would still say
    // « aucune cible » right after they created some, which reads as a product failure.
    const onFocus = () => {
      api.get<TargetsManifest>(`/brands/${brandId}/targets/summary`)
        .then((m) => { if (!cancelled) setTargetsManifest(m); })
        .catch(() => { /* keep whatever we had */ });
    };
    window.addEventListener('focus', onFocus);

    api.get<{ items: { targetId: string; value: { name?: string } }[] }>(`/brands/${brandId}/targets?category=marque&limit=100`)
      .then(({ items }) => {
        if (cancelled) return;
        setMarqueTargets(items
          .filter(i => i.value?.name)
          .map(i => ({ targetId: i.targetId, name: i.value.name! }))
        );
      })
      .catch(() => {});

    // Fetch établissement Cibles targets. The zone data (countryCode, cityName,
    // lat/lng) lives on item.zone (TargetZone), NOT inside item.value.
    api.get<{ items: { zone?: Record<string, unknown> }[] }>(`/brands/${brandId}/targets?category=etablissement&limit=100`)
      .then(({ items }) => {
        if (cancelled) return;
        const seen = new Set<string>();
        const zones: GeoSuggestion[] = [];
        for (const item of items) {
          const z = item.zone;
          if (!z || typeof z !== 'object' || !z.countryCode) continue;
          const sugg: GeoSuggestion = {
            type: (z.type as string) || 'city',
            label: (z.label as string) || (z.cityName as string) || (z.countryCode as string),
            countryCode: z.countryCode as string,
            cityName: z.cityName as string | undefined,
            regionName: z.regionName as string | undefined,
            latitude: z.latitude as number | undefined,
            longitude: z.longitude as number | undefined,
          };
          const key = zoneKeyFrom(sugg as any);
          if (!seen.has(key)) { seen.add(key); zones.push(sugg); }
        }
        setEtabZones(zones);
      })
      .catch(() => { /* non-blocking: no établissements = no chips, graceful */ });

    return () => { cancelled = true; window.removeEventListener('focus', onFocus); };
  }, [open, brandId]);

  // Zone search state
  const [zoneQuery, setZoneQuery] = useState('');
  const [zoneSuggestionSelected, setZoneSuggestionSelected] = useState<GeoSuggestion | null>(null);
  const [zoneLang, setZoneLang] = useState('fr');

  // Reset state when the dialog opens/closes or persona changes.
  useEffect(() => {
    if (open) {
      setState(initialState(persona));
      setError(null);
      setZoneQuery('');
      setZoneSuggestionSelected(null);
    }
  }, [open, persona]);

  const update = useCallback(<K extends keyof EditorState>(key: K, value: EditorState[K]) => {
    setState((prev) => ({ ...prev, [key]: value }));
  }, []);

  const toggleSection = useCallback((n: number) => {
    setState((prev) => {
      const openSections = new Set(prev.openSections);
      if (openSections.has(n)) {
        openSections.delete(n);
      } else {
        openSections.clear();
        openSections.add(n);
      }
      return { ...prev, openSections };
    });
  }, []);

  const advanceSection = useCallback((n: number) => {
    setState((prev) => {
      const openSections = new Set(prev.openSections);
      const doneSections = new Set(prev.doneSections);
      openSections.delete(n);
      doneSections.add(n);
      // Step along SECTION_IDS, not `n + 1` — 3 is no longer an accordion step
      // (it is the always-visible scope strip), so `+ 1` would open nothing.
      const next = SECTION_IDS[SECTION_IDS.indexOf(n) + 1];
      if (next !== undefined) openSections.add(next);
      return { ...prev, openSections, doneSections };
    });
  }, []);

  const addTrait = useCallback((key: TraitKey) => {
    setState((prev) => ({ ...prev, traits: new Set(prev.traits).add(key) }));
  }, []);

  /**
   * Removing a trait also CLEARS its value, and re-adding it starts blank.
   * A hidden-but-live value would keep shaping the persona's prompt, and in a
   * measurement tool the card has to be what actually runs.
   */
  const removeTrait = useCallback((key: TraitKey) => {
    setState((prev) => {
      const traits = new Set(prev.traits);
      traits.delete(key);
      return {
        ...prev,
        traits,
        ...(key === 'tone' ? { tone: 'neutral' as ConversationTone } : { [key]: '' }),
      };
    });
  }, []);

  // Intent change resets maxTurns.
  const handleIntentChange = useCallback((id: ConversationIntentId) => {
    setState((prev) => ({
      ...prev,
      intentId: id,
      maxTurns: CONVERSATION_INTENTS[id].defaultMaxTurns,
    }));
  }, []);

  // Add a zone row from an établissement suggestion chip (no search needed).
  const handleAddZoneFromSuggestion = useCallback((sugg: GeoSuggestion) => {
    const zone = zoneKeyFrom(sugg as any);
    setState((prev) => {
      if (prev.cells.some((c) => c.zone === zone && c.language === zoneLang)) return prev;
      return {
        ...prev,
        cells: [...prev.cells, {
          uid: Math.random().toString(36).slice(2),
          zone,
          zoneSuggestion: sugg,
          language: zoneLang,
          engineRuns: Object.fromEntries(
            CONVERSATION_ENGINES.map((e) => [e, 0]),
          ) as Record<ConversationEngine, number>,
        }],
      };
    });
  }, [zoneLang]);

  // Add a zone row from the current selection.
  const handleAddZone = useCallback(() => {
    if (!zoneSuggestionSelected) return;
    const zone = zoneKeyFrom(zoneSuggestionSelected as any);
    setState((prev) => {
      // Prevent duplicate zones with same language.
      if (prev.cells.some((c) => c.zone === zone && c.language === zoneLang)) return prev;
      const newCell: DraftCell = {
        uid: Math.random().toString(36).slice(2),
        zone,
        zoneSuggestion: zoneSuggestionSelected,
        language: zoneLang,
        engineRuns: Object.fromEntries(
          CONVERSATION_ENGINES.map((e) => [e, 0]),
        ) as Record<ConversationEngine, number>,
      };
      return { ...prev, cells: [...prev.cells, newCell] };
    });
    setZoneQuery('');
    setZoneSuggestionSelected(null);
  }, [zoneSuggestionSelected, zoneLang]);

  const handleRemoveCell = useCallback((uid: string) => {
    setState((prev) => ({ ...prev, cells: prev.cells.filter((c) => c.uid !== uid) }));
  }, []);

  const handleEngineRunChange = useCallback((uid: string, engine: ConversationEngine, delta: number) => {
    setState((prev) => ({
      ...prev,
      cells: prev.cells.map((c) => {
        if (c.uid !== uid) return c;
        const newVal = Math.max(0, Math.min(9, c.engineRuns[engine] + delta));
        return { ...c, engineRuns: { ...c.engineRuns, [engine]: newVal } };
      }),
    }));
  }, []);

  const handleCellLangChange = useCallback((uid: string, lang: string) => {
    setState((prev) => ({
      ...prev,
      cells: prev.cells.map((c) => (c.uid === uid ? { ...c, language: lang } : c)),
    }));
  }, []);

  // Scripted questions
  const handleAddQuestion = useCallback(() => {
    setState((prev) => {
      const nextTurn = (prev.scriptedQuestions[prev.scriptedQuestions.length - 1]?.turn ?? 0) + 1;
      if (nextTurn > prev.maxTurns) return prev;
      return {
        ...prev,
        scriptedQuestions: [...prev.scriptedQuestions, { turn: nextTurn, text: '' }],
      };
    });
  }, []);

  const handleRemoveQuestion = useCallback((idx: number) => {
    setState((prev) => ({
      ...prev,
      scriptedQuestions: prev.scriptedQuestions.filter((_, i) => i !== idx),
    }));
  }, []);

  const handleQuestionTextChange = useCallback((idx: number, text: string) => {
    setState((prev) => ({
      ...prev,
      scriptedQuestions: prev.scriptedQuestions.map((q, i) =>
        i === idx ? { ...q, text } : q,
      ),
    }));
  }, []);

  const cost = useMemo(() => calcCost(state), [state]);
  // Same split as buildPayload — the API caps the list at CONVERSATION_MAX_PERSONA_LIST_ITEMS.
  const decisionCriteriaCount = state.decisionCriteria.split(/[\n,]+/).filter((s) => s.trim()).length;

  const canSubmit =
    state.name.trim().length > 0 &&
    state.cells.length > 0 &&
    decisionCriteriaCount <= CONVERSATION_MAX_PERSONA_LIST_ITEMS &&
    !cost.overCapRuns &&
    !cost.overCapCells;

  const handleSubmit = async (launchAfter: boolean = false) => {
    // D62 — a second launch today replaces today's, without a refund. Asked BEFORE the
    // save so a cancel aborts the whole action: « Enregistrer » alone is right there, so
    // saving anyway would be the surprising half-outcome.
    if (launchAfter && hasLaunchToday(persona?.lastLaunchAt)
      && !window.confirm(t('conversations.runNowSupersedeConfirm'))) return;
    setSaving(true);
    setError(null);
    const payload = buildPayload(state);
    try {
      let result: ConversationPersona;
      if (isEdit && persona) {
        result = await api.patch<ConversationPersona>(
          `/brands/${brandId}/personas/${persona.personaId}`,
          payload,
        );
      } else {
        result = await api.post<ConversationPersona>(
          `/brands/${brandId}/personas`,
          payload,
        );
      }
      if (launchAfter) {
        try {
          const launchResult = await api.post<{ launchId: string }>(
            `/brands/${brandId}/personas/${result.personaId}/run`,
            {},
          );
          onSuccess({ ...result, lastLaunchId: launchResult.launchId, lastLaunchStatus: 'running' });
        } catch {
          // Launch failed but save succeeded — notify with save-only toast.
          onSuccess(result);
        }
      } else {
        onSuccess(result);
      }
      onClose();
    } catch (err) {
      if (err instanceof ApiError) {
        if (err.status === 429) {
          const resetTime = err.body?.resetAt
            ? new Date(err.body.resetAt).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })
            : null;
          setError(resetTime
            ? t('conversations.editorErrorRateLimitReset', { time: resetTime })
            : t('conversations.editorErrorGeneric'));
        } else {
          setError(err.message || t('conversations.editorErrorGeneric'));
        }
      } else {
        setError(t('conversations.editorErrorGeneric'));
      }
    } finally {
      setSaving(false);
    }
  };

  // ---------------------------------------------------------------------------
  // Section renders
  // ---------------------------------------------------------------------------

  /**
   * One optional trait's control. Bodies are the originals, unchanged — only their
   * container moved, so the four behavioural tooltips stay where users expect them.
   */
  const renderTraitControl = (key: TraitKey) => {
    switch (key) {
      case 'tone':
        return (
          <FormControl size="small" fullWidth>
            <InputLabel>{t('conversations.editorToneLabel')}</InputLabel>
            <Select
              label={t('conversations.editorToneLabel')}
              value={state.tone}
              onChange={(e) => update('tone', e.target.value as ConversationTone)}
            >
              {TONE_KEYS.map((k) => (
                <MenuItem key={k} value={k}>{t(`conversations.editorTone${k.charAt(0).toUpperCase() + k.slice(1)}`)}</MenuItem>
              ))}
            </Select>
          </FormControl>
        );
      case 'ageRange':
        return (
          <FormControl size="small" fullWidth>
            <InputLabel>{t('conversations.editorAgeLabel')}</InputLabel>
            <Select
              label={t('conversations.editorAgeLabel')}
              value={state.ageRange}
              onChange={(e) => update('ageRange', e.target.value)}
            >
              <MenuItem value="">—</MenuItem>
              {['18-25 ans', '26-34 ans', '35-44 ans', '45-54 ans', '55 ans et plus'].map((a) => (
                <MenuItem key={a} value={a}>{a}</MenuItem>
              ))}
            </Select>
          </FormControl>
        );
      case 'gender':
        return (
          <FormControl size="small" fullWidth>
            <InputLabel>{t('conversations.editorGenderLabel')}</InputLabel>
            <Select
              label={t('conversations.editorGenderLabel')}
              value={state.gender}
              onChange={(e) => update('gender', e.target.value as EditorState['gender'])}
            >
              <MenuItem value="">—</MenuItem>
              <MenuItem value="female">{t('conversations.editorGenderFemale')}</MenuItem>
              <MenuItem value="male">{t('conversations.editorGenderMale')}</MenuItem>
              <MenuItem value="unspecified">{t('conversations.editorGenderUnspecified')}</MenuItem>
            </Select>
          </FormControl>
        );
      case 'budget':
        return (
          <TextField
            label={t('conversations.editorBudgetLabel')}
            size="small"
            fullWidth
            value={state.budget}
            onChange={(e) => update('budget', e.target.value)}
            inputProps={{ maxLength: CONVERSATION_MAX_PERSONA_BUDGET_CHARS }}
          />
        );
      case 'selectionFocus':
        return (
          <FormControl size="small" fullWidth>
            <InputLabel sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              {t('conversations.editorSelectionFocusLabel')}
              <Tooltip title={t('conversations.editorSelectionFocusTooltip')} placement="top">
                <InfoOutlinedIcon sx={{ fontSize: 14, ml: 0.25 }} />
              </Tooltip>
            </InputLabel>
            <Select
              label={t('conversations.editorSelectionFocusLabel') + ' i'}
              value={state.selectionFocus}
              onChange={(e) => update('selectionFocus', e.target.value as EditorState['selectionFocus'])}
            >
              <MenuItem value="">{t('conversations.editorSelectionFocusNone')}</MenuItem>
              <MenuItem value="broad">{t('conversations.editorSelectionFocusBroad')}</MenuItem>
              <MenuItem value="focused">{t('conversations.editorSelectionFocusFocused')}</MenuItem>
            </Select>
          </FormControl>
        );
      case 'domainExpertise':
        return (
          <FormControl size="small" fullWidth>
            <InputLabel sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              {t('conversations.editorDomainExpertiseLabel')}
              <Tooltip title={t('conversations.editorDomainExpertiseTooltip')} placement="top">
                <InfoOutlinedIcon sx={{ fontSize: 14, ml: 0.25 }} />
              </Tooltip>
            </InputLabel>
            <Select
              label={t('conversations.editorDomainExpertiseLabel') + ' i'}
              value={state.domainExpertise}
              onChange={(e) => update('domainExpertise', e.target.value as EditorState['domainExpertise'])}
            >
              <MenuItem value="">{t('conversations.editorDomainExpertiseNone')}</MenuItem>
              <MenuItem value="novice">{t('conversations.editorDomainExpertiseNovice')}</MenuItem>
              <MenuItem value="intermediate">{t('conversations.editorDomainExpertiseIntermediate')}</MenuItem>
              <MenuItem value="expert">{t('conversations.editorDomainExpertiseExpert')}</MenuItem>
            </Select>
          </FormControl>
        );
      case 'languageStyle':
        return (
          <FormControl size="small" fullWidth>
            <InputLabel sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              {t('conversations.editorLanguageStyleLabel')}
              <Tooltip title={t('conversations.editorLanguageStyleTooltip')} placement="top">
                <InfoOutlinedIcon sx={{ fontSize: 14, ml: 0.25 }} />
              </Tooltip>
            </InputLabel>
            <Select
              label={t('conversations.editorLanguageStyleLabel') + ' i'}
              value={state.languageStyle}
              onChange={(e) => update('languageStyle', e.target.value as EditorState['languageStyle'])}
            >
              <MenuItem value="">{t('conversations.editorLanguageStyleNone')}</MenuItem>
              <MenuItem value="professional">{t('conversations.editorLanguageStyleProfessional')}</MenuItem>
              <MenuItem value="casual">{t('conversations.editorLanguageStyleCasual')}</MenuItem>
              <MenuItem value="typos">{t('conversations.editorLanguageStyleTypos')}</MenuItem>
            </Select>
          </FormControl>
        );
      case 'receptiveness':
        return (
          <FormControl size="small" fullWidth>
            <InputLabel sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              {t('conversations.editorReceptivenessLabel')}
              <Tooltip title={t('conversations.editorReceptivenessTooltip')} placement="top">
                <InfoOutlinedIcon sx={{ fontSize: 14, ml: 0.25 }} />
              </Tooltip>
            </InputLabel>
            <Select
              label={t('conversations.editorReceptivenessLabel') + ' i'}
              value={state.receptiveness}
              onChange={(e) => update('receptiveness', e.target.value as EditorState['receptiveness'])}
            >
              <MenuItem value="">{t('conversations.editorReceptivenessNone')}</MenuItem>
              <MenuItem value="accepting">{t('conversations.editorReceptivenessAccepting')}</MenuItem>
              <MenuItem value="curious">{t('conversations.editorReceptivenessCurious')}</MenuItem>
              <MenuItem value="challenger">{t('conversations.editorReceptivenessChallenger')}</MenuItem>
            </Select>
          </FormControl>
        );
    }
  };

  const renderSection1 = () => {
    const activeTraits = TRAITS.filter((tr) => state.traits.has(tr.key));
    const available = TRAITS.filter((tr) => !state.traits.has(tr.key));

    return (
      <Stack spacing={2}>
        <TextField
          label={t('conversations.editorNameLabel')}
          fullWidth
          size="small"
          value={state.name}
          onChange={(e) => update('name', e.target.value)}
          required
          inputProps={{ maxLength: CONVERSATION_MAX_PERSONA_NAME_CHARS }}
        />

        {/*
          The brief panel. The explanation used to be `helperText` under the textarea —
          12px grey, which users skipped — but conceptually the FIELD is subordinate to
          the brief, not the other way round, so the fix is containment rather than
          louder type. No Alert here on purpose: this dialog uses Alert for STATE
          (Cibles empty, scripted mode, save error) and a permanent info Alert would
          dilute that signal.
        */}
        <Box
          sx={{
            borderRadius: 2,
            overflow: 'hidden',
            border: '1px solid',
            borderColor: briefRuleViolated ? 'error.main' : 'divider',
            borderLeft: '4px solid',
            borderLeftColor: briefRuleViolated ? 'error.main' : 'primary.main',
            bgcolor: 'background.paper',
          }}
        >
          <Box sx={{ px: 2, pt: 1.5, pb: 1.25, bgcolor: (th) => alpha(th.palette.primary.main, 0.06) }}>
            <Typography id="persona-brief-label" variant="subtitle2" fontWeight={700} sx={{ lineHeight: 1.3 }}>
              {t('conversations.editorSituationLabel')}
            </Typography>
            <Typography variant="body2" sx={{ mt: 0.5, maxWidth: '62ch', lineHeight: 1.5 }}>
              {t('conversations.editorSituationHelp')}
            </Typography>
          </Box>
          <TextField
            fullWidth
            multiline
            rows={3}
            size="small"
            value={state.description}
            onChange={(e) => update('description', e.target.value)}
            error={briefRuleViolated}
            inputProps={{
              'aria-labelledby': 'persona-brief-label',
              maxLength: CONVERSATION_MAX_PERSONA_DESCRIPTION_CHARS,
            }}
            sx={{
              '& .MuiOutlinedInput-root': { borderRadius: 0 },
              '& fieldset': { border: 'none' },
              // `fieldset: none` also removes MUI's focus ring, so focus gets its own
              // affordance rather than none at all.
              '& .MuiOutlinedInput-root.Mui-focused': {
                bgcolor: (th) => alpha(th.palette.primary.main, 0.04),
              },
            }}
          />
        </Box>

        <TextField
          label={t('conversations.editorDecisionCriteriaLabel')}
          fullWidth
          multiline
          rows={2}
          size="small"
          value={state.decisionCriteria}
          onChange={(e) => update('decisionCriteria', e.target.value.slice(0, 500))}
          placeholder={t('conversations.editorDecisionCriteriaPlaceholder')}
          error={decisionCriteriaCount > CONVERSATION_MAX_PERSONA_LIST_ITEMS}
          helperText={`${decisionCriteriaCount}/${CONVERSATION_MAX_PERSONA_LIST_ITEMS} · ${state.decisionCriteria.length}/500`}
          inputProps={{ maxLength: 500 }}
        />

        {activeTraits.length > 0 && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
            {activeTraits.map((tr) => (
              <Box key={tr.key} sx={{ position: 'relative', '&:hover .trait-remove': { opacity: 1 } }}>
                {renderTraitControl(tr.key)}
                <Tooltip title={t('conversations.editorTraitRemove')}>
                  <IconButton
                    className="trait-remove"
                    size="small"
                    aria-label={t('conversations.editorTraitRemove')}
                    onClick={() => removeTrait(tr.key)}
                    sx={{
                      position: 'absolute',
                      top: -10,
                      right: -8,
                      p: 0.25,
                      bgcolor: 'background.paper',
                      border: '1px solid',
                      borderColor: 'divider',
                      opacity: 0.6,
                      transition: 'opacity 120ms',
                      '&:hover': { opacity: 1 },
                      '&:focus-visible': { opacity: 1 },
                    }}
                  >
                    <CloseIcon sx={{ fontSize: 13 }} />
                  </IconButton>
                </Tooltip>
              </Box>
            ))}
          </Box>
        )}

        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
          <Button
            size="small"
            variant="outlined"
            startIcon={<AddIcon />}
            disabled={available.length === 0}
            onClick={(e) => setTraitMenuAnchor(e.currentTarget)}
          >
            {t('conversations.editorAddTrait')}
          </Button>
          <Typography variant="caption" color="text.secondary">
            {t('conversations.editorAddTraitHelp')}
          </Typography>
          <Button
            size="small"
            variant="text"
            sx={{ ml: 'auto' }}
            onClick={() => setState((p) => ({
              ...p,
              name: '', description: '', decisionCriteria: '',
              // Clearing the card means clearing the traits too, values included —
              // leaving a stale value behind a removed trait is the bug this whole
              // picker exists to prevent.
              traits: new Set(),
              tone: 'neutral', budget: '', ageRange: '', gender: '',
              selectionFocus: '', domainExpertise: '', languageStyle: '', receptiveness: '',
            }))}
          >
            {t('conversations.editorClearFields')}
          </Button>
        </Box>

        <Menu
          anchorEl={traitMenuAnchor}
          open={Boolean(traitMenuAnchor)}
          onClose={() => setTraitMenuAnchor(null)}
          slotProps={{ paper: { sx: { maxWidth: 330 } } }}
        >
          {(['profile', 'behaviour'] as const).flatMap((group) => {
            const items = available.filter((tr) => tr.group === group);
            if (items.length === 0) return [];
            return [
              <ListSubheader key={`h-${group}`} sx={{ fontSize: '0.6875rem', textTransform: 'uppercase', letterSpacing: '0.06em', lineHeight: 2.2 }}>
                {t(group === 'profile' ? 'conversations.editorTraitGroupProfile' : 'conversations.editorTraitGroupBehaviour')}
              </ListSubheader>,
              ...items.map((tr) => (
                <MenuItem
                  key={tr.key}
                  onClick={() => { addTrait(tr.key); setTraitMenuAnchor(null); }}
                  sx={{ alignItems: 'flex-start', py: 0.75 }}
                >
                  <ListItemIcon sx={{ minWidth: 30, mt: '2px' }}>
                    <tr.Icon sx={{ fontSize: 18 }} />
                  </ListItemIcon>
                  <ListItemText
                    primary={t(tr.labelKey)}
                    secondary={t(tr.descKey)}
                    primaryTypographyProps={{ fontSize: '0.875rem', fontWeight: 600 }}
                    secondaryTypographyProps={{ fontSize: '0.75rem', whiteSpace: 'normal' }}
                  />
                </MenuItem>
              )),
            ];
          })}
        </Menu>
      </Stack>
    );
  };

  const renderSection2 = () => {
    const showQuestions = state.mode !== 'improvised';

    return (
      <Stack spacing={2}>
        {/* Intent cards */}
        <Box>
          <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ mb: 1, display: 'block' }}>
            {t('conversations.editorIntentLabel')}
          </Typography>
          <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 1.5 }}>
            {(['learn', 'discover', 'compare'] as ConversationIntentId[]).map((id) => {
              const it = CONVERSATION_INTENTS[id];
              const active = state.intentId === id;
              return (
                <Box
                  key={id}
                  component="button"
                  type="button"
                  role="radio"
                  aria-checked={active}
                  onClick={() => handleIntentChange(id)}
                  sx={{
                    border: active ? '1.5px solid' : '1.5px solid',
                    borderColor: active ? 'primary.main' : 'divider',
                    borderRadius: 2,
                    p: 1.5,
                    background: active ? 'rgba(124,58,237,0.07)' : 'background.paper',
                    cursor: 'pointer',
                    textAlign: 'left',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 0.75,
                    '&:hover': { borderColor: 'primary.light' },
                  }}
                >
                  <Chip label={it.name} size="small" color={active ? 'primary' : 'default'} sx={{ alignSelf: 'flex-start', fontSize: '0.6875rem', fontWeight: 600 }} />
                  <Typography variant="caption" color="text.secondary">{it.exampleQuestion}</Typography>
                  <Typography variant="caption">{it.description.slice(0, 80)}…</Typography>
                  <Typography variant="caption" color="text.secondary">
                    {t('conversations.editorIntentWin')} {it.outcomes.win.toLowerCase()}
                  </Typography>
                </Box>
              );
            })}
          </Box>
        </Box>

        {state.intentId === 'compare' && marqueTargets.length > 0 && (
          <Box>
            <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ mb: 1, display: 'block' }}>
              {t('conversations.editorComparatorLabel')}
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
              {marqueTargets.map(m => {
                const selected = state.comparatorTargetIds.includes(m.targetId);
                return (
                  <Chip
                    key={m.targetId}
                    label={m.name}
                    size="small"
                    onClick={() => setState(prev => ({
                      ...prev,
                      comparatorTargetIds: selected
                        ? prev.comparatorTargetIds.filter(id => id !== m.targetId)
                        : [...prev.comparatorTargetIds, m.targetId],
                    }))}
                    color={selected ? 'primary' : 'default'}
                    variant={selected ? 'filled' : 'outlined'}
                    sx={{ cursor: 'pointer' }}
                  />
                );
              })}
            </Box>
            <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
              {t('conversations.editorComparatorHelp')}
            </Typography>
          </Box>
        )}

        {/* Conduct mode */}
        <Box>
          <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ mb: 1, display: 'block' }}>
            {t('conversations.editorModeTitle')}
          </Typography>
          <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 1.5 }}>
            {(['improvised', 'mixed', 'scripted'] as ConversationMode[]).map((m) => {
              const active = state.mode === m;
              const label = t(`conversations.editorMode${m.charAt(0).toUpperCase() + m.slice(1)}`);
              const desc = m === 'scripted'
                ? t('conversations.editorModeScriptedDesc', { n: state.scriptedQuestions.length || state.maxTurns })
                : t(`conversations.editorMode${m.charAt(0).toUpperCase() + m.slice(1)}Desc`);
              return (
                <Box
                  key={m}
                  component="button"
                  type="button"
                  onClick={() => update('mode', m)}
                  sx={{
                    border: active ? '1.5px solid' : '1.5px dashed',
                    borderColor: active ? 'primary.main' : 'divider',
                    borderRadius: 2,
                    p: 1.5,
                    background: active ? 'rgba(124,58,237,0.07)' : 'background.paper',
                    cursor: 'pointer',
                    textAlign: 'left',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 0.5,
                    '&:hover': { borderColor: active ? 'primary.main' : 'text.disabled' },
                  }}
                >
                  <Typography fontWeight={600} fontSize="0.875rem">
                    {label} {m === 'improvised' && <Typography component="span" variant="caption" color="text.secondary">{t('conversations.editorModeRecommended')}</Typography>}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">{desc}</Typography>
                </Box>
              );
            })}
          </Box>
        </Box>

        {/* Starting prompt (simple form of mixed — one pinned turn 1) */}
        {state.mode === 'improvised' && (
          <TextField
            label={t('conversations.editorStartingPromptLabel')}
            fullWidth
            multiline
            rows={2}
            size="small"
            value={state.startingPrompt}
            onChange={(e) => update('startingPrompt', e.target.value)}
            placeholder={t('conversations.editorStartingPromptPlaceholder')}
            helperText={t('conversations.editorStartingPromptHelp')}
            inputProps={{ maxLength: CONVERSATION_MAX_MESSAGE_CHARS }}
          />
        )}

        {/* Scripted / mixed questions */}
        {showQuestions && (
          <Stack spacing={1.5}>
            <Typography variant="caption" color="text.secondary" fontWeight={600}>
              {state.mode === 'scripted' ? t('conversations.editorQuestionsScripted') : t('conversations.editorQuestionsMixed')}
            </Typography>
            {(() => {
              const byTurn = new Map<number, { idx: number; text: string }[]>();
              state.scriptedQuestions.forEach((q, idx) => {
                if (!byTurn.has(q.turn)) byTurn.set(q.turn, []);
                byTurn.get(q.turn)!.push({ idx, text: q.text });
              });

              return [...byTurn.entries()].sort(([a], [b]) => a - b).map(([turn, entries]) => (
                <Box key={turn} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1.5, p: 1.5, bgcolor: 'background.default' }}>
                  <Typography variant="caption" color="text.secondary" fontWeight={700} sx={{ display: 'block', mb: 1 }}>
                    Tour {turn} {state.mode === 'mixed' && entries.length > 1 && (
                      <Typography component="span" variant="caption" color="primary.main">
                        {` — le moteur choisira parmi ${entries.length} alternatives`}
                      </Typography>
                    )}
                  </Typography>
                  {entries.map(({ idx, text }) => (
                    <Box key={idx} sx={{ display: 'flex', gap: 1, alignItems: 'flex-start', mb: 1 }}>
                      <TextField
                        fullWidth
                        size="small"
                        multiline
                        rows={2}
                        value={text}
                        onChange={(e) => handleQuestionTextChange(idx, e.target.value)}
                        placeholder={t('conversations.editorScriptedQuestionsPlaceholder')}
                        inputProps={{ maxLength: 400 }}
                      />
                      <IconButton size="small" onClick={() => handleRemoveQuestion(idx)} sx={{ mt: 0.5 }}>
                        <DeleteOutlineIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  ))}
                  {state.mode === 'mixed' && (
                    <Button
                      size="small"
                      startIcon={<AddIcon />}
                      onClick={() => setState(prev => ({
                        ...prev,
                        scriptedQuestions: [...prev.scriptedQuestions, { turn, text: '' }],
                      }))}
                      sx={{ fontSize: '0.75rem' }}
                    >
                      {t('conversations.editorAddAlternative')}
                    </Button>
                  )}
                </Box>
              ));
            })()}
            {state.scriptedQuestions.length < state.maxTurns && (
              <Button
                size="small"
                startIcon={<AddIcon />}
                variant="outlined"
                onClick={handleAddQuestion}
                sx={{ alignSelf: 'flex-start' }}
              >
                {t('conversations.editorScriptedQuestionsAdd')}
              </Button>
            )}
          </Stack>
        )}

        {/* Starting prompt for non-improvised if needed */}
        {state.mode !== 'improvised' && (
          <TextField
            label={t('conversations.editorStartingPromptLabel')}
            fullWidth
            multiline
            rows={2}
            size="small"
            value={state.startingPrompt}
            onChange={(e) => update('startingPrompt', e.target.value)}
            placeholder={t('conversations.editorStartingPromptPlaceholder')}
            helperText={t('conversations.editorStartingPromptHelp')}
            inputProps={{ maxLength: CONVERSATION_MAX_MESSAGE_CHARS }}
          />
        )}
      </Stack>
    );
  };

  /**
   * §7.8 tag card. It sat above the accordions until 2026-10-03, which put the least
   * important field of the form first; it now lives in the right metadata column.
   * The two outlined chips are DERIVED from `intentId`/`mode` for display only and are
   * never stored, so `state.tags.length` — not the rendered chip count — is what the
   * `CONVERSATION_MAX_TAGS` budget counts.
   */
  const renderTagsCard = () => (
    <Box
      sx={{
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 2,
        bgcolor: 'background.paper',
        p: 2,
        display: 'flex',
        flexDirection: 'column',
        gap: 1,
      }}
    >
      <Typography variant="overline" color="text.secondary" sx={{ fontSize: '0.6875rem' }}>
        Tags
      </Typography>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, alignItems: 'center' }}>
        {state.tags.map((tag) => (
          <Chip
            key={tag}
            label={tag}
            size="small"
            onDelete={() =>
              setState((s) => ({ ...s, tags: s.tags.filter((t) => t !== tag) }))
            }
            sx={{ borderRadius: '5px', fontSize: '0.75rem' }}
          />
        ))}
        {/* derived (automatic) tags — display-only, never stored */}
        <Chip
          key={`intent:${state.intentId}`}
          label={`intention : ${state.intentId}`}
          size="small"
          variant="outlined"
          sx={{ borderRadius: '5px', fontSize: '0.75rem', opacity: 0.55, cursor: 'default' }}
        />
        <Chip
          key={`mode:${state.mode}`}
          label={`mode : ${state.mode}`}
          size="small"
          variant="outlined"
          sx={{ borderRadius: '5px', fontSize: '0.75rem', opacity: 0.55, cursor: 'default' }}
        />
      </Box>
      {state.tags.length < CONVERSATION_MAX_TAGS && (
        <TextField
          size="small"
          fullWidth
          value={tagInput}
          placeholder="Ajouter un tag… (Entrée)"
          onChange={(e) => {
            // Enforce lowercase-kebab on input: spaces → hyphens, strip invalid chars
            const raw = e.target.value
              .toLowerCase()
              .replace(/\s+/g, '-')
              .replace(/[^a-z0-9-]/g, '');
            setTagInput(raw);
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ',') {
              e.preventDefault();
              const tag = tagInput.replace(/^-+|-+$/g, '').slice(0, 30);
              if (tag.length >= 1 && !state.tags.includes(tag)) {
                setState((s) => ({ ...s, tags: [...s.tags, tag] }));
              }
              setTagInput('');
            } else if (e.key === 'Backspace' && tagInput === '' && state.tags.length > 0) {
              setState((s) => ({ ...s, tags: s.tags.slice(0, -1) }));
            }
          }}
          onBlur={() => {
            // Commit on blur too (same as Enter)
            const tag = tagInput.replace(/^-+|-+$/g, '').slice(0, 30);
            if (tag.length >= 1 && !state.tags.includes(tag)) {
              setState((s) => ({ ...s, tags: [...s.tags, tag] }));
            }
            setTagInput('');
          }}
          helperText={`${state.tags.length} / ${CONVERSATION_MAX_TAGS} — minuscules, chiffres, tirets`}
          FormHelperTextProps={{ sx: { mx: 0 } }}
        />
      )}
    </Box>
  );

  /**
   * « Qu'est-ce qu'on mesure ? » — NOT a numbered wizard step any more (2026-10-03).
   *
   * It was a read-only recap behind a chevron: a configured user had to click to learn
   * nothing new, and an unconfigured user never saw the one thing worth saying. As an
   * always-visible strip the configured case answers itself in one line, and the empty
   * case becomes the discovery moment it should always have been.
   *
   * It then moved out of the step column entirely (2026-10-03) and into the right
   * metadata column, next to the tag card: it is not a choice the user makes here, so
   * it has no business interrupting the numbered steps.
   *
   * Two category lists on purpose: the chip row iterates the SHARED `TARGET_CATEGORIES`
   * (7, filtered on `n > 0`, so legacy `offre` rows still surface), while the empty-state
   * pitch iterates the frontend `CATEGORIES` (6 — `offre`'s tab was retired 2026-09-23)
   * so it never advertises a category with no tab to fill in.
   */
  const renderScopeStrip = () => {
    const counted = TARGET_CATEGORIES
      .map((cat) => ({ cat, n: catCount(targetsManifest, cat) }))
      .filter(({ n }) => n > 0)
      .sort((a, b) => b.n - a.n);
    const shown = counted.slice(0, 4);
    const rest = counted.slice(4);

    return (
      <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, bgcolor: 'background.paper', p: 2 }}>
        {/* Not loaded (or the fetch failed): never fall through to the empty-state
            pitch, which would tell a configured user they have nothing. */}
        {!targetsManifest && (
          <Stack direction="row" spacing={1} alignItems="center">
            <Typography variant="caption" color="text.secondary" fontWeight={600}>
              {t('conversations.editorScopeLabel')}
            </Typography>
            <Skeleton variant="rounded" width={120} height={20} />
          </Stack>
        )}

        {targetsManifest && targetsManifest.targetCount > 0 && (
          <Stack direction="row" spacing={0.75} alignItems="center" flexWrap="wrap" useFlexGap>
            <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ mr: 0.5 }}>
              {t('conversations.editorScopeLabel')}
            </Typography>
            {shown.map(({ cat, n }) => (
              <Chip
                key={cat}
                size="small"
                variant="outlined"
                label={`${t(`cibles.tab.${cat}`)} ${n.toLocaleString('fr-FR')}`}
                sx={{ fontSize: '0.6875rem', fontWeight: 600 }}
              />
            ))}
            {rest.length > 0 && (
              <Tooltip title={rest.map(({ cat, n }) => `${t(`cibles.tab.${cat}`)} ${n}`).join(' · ')}>
                <Chip
                  size="small"
                  variant="outlined"
                  label={t('conversations.editorScopeMore', { count: rest.length })}
                  sx={{ fontSize: '0.6875rem' }}
                />
              </Tooltip>
            )}
            <Typography variant="caption" color="text.secondary">
              {t('conversations.editorCiblesCount', { count: targetsManifest.targetCount })}
            </Typography>
            <Tooltip title={t('conversations.editorScopeInfo')} placement="top">
              <InfoOutlinedIcon sx={{ fontSize: 14, color: 'text.disabled' }} />
            </Tooltip>
            <Link
              href="/geo-strategy#cibles"
              target="_blank"
              rel="noopener"
              underline="hover"
              variant="caption"
              aria-label={t('conversations.editorScopeNewTab')}
              sx={{ ml: 'auto', fontWeight: 600, display: 'inline-flex', alignItems: 'center', gap: 0.25 }}
            >
              {t('conversations.editorCiblesManage')}
              <OpenInNewIcon sx={{ fontSize: 12 }} />
            </Link>
          </Stack>
        )}

        {/* The discovery moment. Deliberately NOT an Alert and not the warning palette:
            an optional feature the user has not set up yet is not an error. */}
        {targetsManifest && targetsManifest.targetCount === 0 && (
          <Stack spacing={1.5}>
            <Box>
              <Typography fontSize="0.875rem" fontWeight={600}>
                {t('conversations.editorScopeEmptyTitle', { n: CATEGORIES.length })}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {t('conversations.editorScopeEmptyBody')}
              </Typography>
            </Box>
            <Box sx={{ display: 'grid', gap: 1, gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))' }}>
              {CATEGORIES.map(({ id, Icon, color }) => (
                <Stack
                  key={id}
                  direction="row"
                  spacing={1}
                  alignItems="flex-start"
                  sx={{ border: '1px dashed', borderColor: 'divider', borderRadius: 1.5, px: 1.25, py: 1, bgcolor: 'action.hover' }}
                >
                  <Icon sx={{ fontSize: 16, mt: '1px', color: alpha(color, 0.55) }} />
                  <Box sx={{ minWidth: 0 }}>
                    <Typography variant="caption" fontWeight={600} sx={{ display: 'block', lineHeight: 1.3 }}>
                      {t(`cibles.tab.${id}`)}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.6875rem', lineHeight: 1.3 }}>
                      {t(`cibles.desc.${id}`)}
                    </Typography>
                  </Box>
                </Stack>
              ))}
            </Box>
            <Stack direction="row" spacing={1.5} alignItems="center" flexWrap="wrap" useFlexGap>
              {/* A real anchor, opening a new tab: the modal is local state with no
                  draft path (saving needs a zone from the last section), so navigating
                  this tab would throw the form away. */}
              <Button
                size="small"
                variant="contained"
                component="a"
                href="/geo-strategy#cibles"
                target="_blank"
                rel="noopener"
                aria-label={t('conversations.editorScopeNewTab')}
                endIcon={<OpenInNewIcon sx={{ fontSize: 14 }} />}
              >
                {t('conversations.editorScopeEmptyCta')}
              </Button>
              <Typography variant="caption" color="text.secondary">
                {t('conversations.editorScopeEmptyConsequence')}
              </Typography>
            </Stack>
          </Stack>
        )}
      </Box>
    );
  };

  /**
   * « Quand s'arrête-t-il ? » — reads as one sentence (2026-10-03).
   *
   * The old card showed `terminer_la_conversation(raison)` in a monospace chip. The
   * audience is a marketing user: an LLM tool-call signature is an implementation
   * detail, and naming it taught nothing the prose below doesn't. No grey box, no chip.
   *
   * `stopCriterion` is SOFT — it replaces the intent's exit-condition prose in the
   * persona's system prompt and the model judges when it is met. `maxTurns` is the only
   * hard stop, which is why the two selects sit on one row with the ceiling's help line
   * saying outright that it always wins.
   */
  const renderSection4 = () => {
    const intent = CONVERSATION_INTENTS[state.intentId];
    const isScripted = state.mode === 'scripted';
    return (
      <Stack spacing={1.5}>
        <Typography variant="body2" color="text.secondary">
          {isScripted
            ? t('conversations.editorStopScripted', { n: state.scriptedQuestions.length || state.maxTurns })
            : t('conversations.editorStopIntro')}
        </Typography>
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'flex-start' }}>
          {!isScripted && (
            <FormControl size="small" sx={{ flex: '1 1 260px', minWidth: 260 }}>
              <InputLabel>{t('conversations.editorStopWhenLabel')}</InputLabel>
              <Select
                label={t('conversations.editorStopWhenLabel')}
                value={state.stopCriterion}
                onChange={(e) => update('stopCriterion', e.target.value as EditorState['stopCriterion'])}
              >
                <MenuItem value="">{t('conversations.editorStopDefault', { intent: intent.name.toLowerCase() })}</MenuItem>
                {CONVERSATION_STOP_CRITERIA.map((id) => (
                  <MenuItem key={id} value={id}>{t(`conversations.editorStop.${id}`)}</MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
          <FormControl size="small" sx={{ flex: '0 0 170px' }}>
            <InputLabel>{t('conversations.editorStopCeilingLabel')}</InputLabel>
            <Select
              label={t('conversations.editorStopCeilingLabel')}
              value={state.maxTurns}
              onChange={(e) => update('maxTurns', Number(e.target.value))}
              disabled={isScripted}
            >
              {[3, 4, 5, 6].map((n) => (
                <MenuItem key={n} value={n}>{t('conversations.editorStopTurns', { count: n })}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
        <Typography variant="caption" color="text.secondary">
          {t('conversations.editorStopCeilingHelp', { n: state.maxTurns })}
        </Typography>
      </Stack>
    );
  };

  const renderSection5 = () => {
    const totalRuns = state.cells.reduce(
      (s, c) => s + CONVERSATION_ENGINES.reduce((a, e) => a + c.engineRuns[e], 0),
      0,
    );
    const totalConfigs = state.cells.reduce(
      (s, c) => s + CONVERSATION_ENGINES.filter((e) => c.engineRuns[e] > 0).length,
      0,
    );

    return (
      <Stack spacing={2}>
        <Typography variant="caption" color="text.secondary">
          {t('conversations.editorMatrixInstructions')}
        </Typography>

        {/* Zone × engine matrix */}
        {state.cells.length > 0 && (
          <Box sx={{ overflowX: 'auto' }}>
            <Box component="table" sx={{ borderCollapse: 'collapse', width: '100%', fontSize: '0.8125rem' }}>
              <thead>
                <Box component="tr">
                  <Box component="th" sx={{ textAlign: 'left', p: '6px 8px', fontWeight: 600, color: 'text.secondary', fontSize: '0.6875rem', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                    {t('conversations.editorMatrixZone')}
                  </Box>
                  <Box component="th" sx={{ textAlign: 'left', p: '6px 8px', fontWeight: 600, color: 'text.secondary', fontSize: '0.6875rem', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                    {t('conversations.editorMatrixLang')}
                  </Box>
                  {CONVERSATION_ENGINES.map((e) => (
                    <Box component="th" key={e} sx={{ textAlign: 'center', p: '6px 8px', fontWeight: 600, color: 'text.secondary', fontSize: '0.6875rem', textTransform: 'uppercase' }}>
                      {ENGINE_LABELS[e]}
                    </Box>
                  ))}
                  <Box component="th" sx={{ textAlign: 'center', p: '6px 8px', fontWeight: 600, color: 'text.secondary', fontSize: '0.6875rem', textTransform: 'uppercase' }}>
                    {t('conversations.editorMatrixRuns')}
                  </Box>
                </Box>
              </thead>
              <tbody>
                {state.cells.map((cell) => {
                  const rowTotal = CONVERSATION_ENGINES.reduce((s, e) => s + cell.engineRuns[e], 0);
                  return (
                    <Box component="tr" key={cell.uid} sx={{ borderTop: '1px solid', borderColor: 'divider' }}>
                      {/* Zone: flag + name + type chip + remove */}
                      <Box component="td" sx={{ p: '8px', whiteSpace: 'nowrap' }}>
                        <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75 }}>
                          <span style={{ fontSize: '1.1em', lineHeight: 1 }}>
                            {flagEmoji(cell.zoneSuggestion.countryCode ?? 'XX')}
                          </span>
                          <Typography variant="body2" fontWeight={500} sx={{ whiteSpace: 'nowrap' }}>
                            {cell.zoneSuggestion.label}
                          </Typography>
                          <Chip
                            label={ZONE_TYPE_FR[cell.zoneSuggestion.type ?? ''] ?? cell.zoneSuggestion.type ?? '?'}
                            size="small"
                            sx={{ height: 18, fontSize: '0.6rem', fontWeight: 600, letterSpacing: '0.03em' }}
                          />
                          <Button
                            size="small"
                            onClick={() => handleRemoveCell(cell.uid)}
                            sx={{ minWidth: 0, fontSize: '0.75rem', color: 'text.disabled', p: '0 4px' }}
                          >
                            ×
                          </Button>
                        </Box>
                      </Box>
                      <Box component="td" sx={{ p: '4px 8px' }}>
                        <FormControl size="small">
                          <Select
                            value={cell.language}
                            onChange={(e) => handleCellLangChange(cell.uid, e.target.value)}
                            sx={{ fontSize: '0.8125rem' }}
                          >
                            {LANG_OPTIONS.map((l) => (
                              <MenuItem key={l.value} value={l.value}>{l.flag} {l.label}</MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      </Box>
                      {CONVERSATION_ENGINES.map((eng) => (
                        <Box component="td" key={eng} sx={{ textAlign: 'center', p: '4px 8px' }}>
                          <Box sx={{ display: 'inline-flex', alignItems: 'center', border: '1px solid', borderColor: 'divider', borderRadius: 1, overflow: 'hidden' }}>
                            <Box component="button" type="button"
                              aria-label="Moins"
                              onClick={() => handleEngineRunChange(cell.uid, eng, -1)}
                              sx={{ border: 'none', background: 'transparent', width: 24, height: 28, cursor: 'pointer', color: 'text.secondary', '&:hover': { bgcolor: 'action.hover' } }}>
                              −
                            </Box>
                            <Box component="span" sx={{ minWidth: 18, textAlign: 'center', fontWeight: 600, color: cell.engineRuns[eng] === 0 ? 'text.disabled' : 'text.primary' }}>
                              {cell.engineRuns[eng]}
                            </Box>
                            <Box component="button" type="button"
                              aria-label="Plus"
                              onClick={() => handleEngineRunChange(cell.uid, eng, 1)}
                              sx={{ border: 'none', background: 'transparent', width: 24, height: 28, cursor: 'pointer', color: 'text.secondary', '&:hover': { bgcolor: 'action.hover' } }}>
                              +
                            </Box>
                          </Box>
                        </Box>
                      ))}
                      <Box component="td" sx={{ textAlign: 'center', p: '8px', fontWeight: 600 }}>
                        {rowTotal}
                      </Box>
                    </Box>
                  );
                })}
                {/* Totals row */}
                <Box component="tr" sx={{ borderTop: '1px solid', borderColor: 'divider', color: 'text.secondary', fontSize: '0.75rem' }}>
                  <Box component="td" sx={{ p: '6px 8px' }}>{t('conversations.editorMatrixPerMoteur')}</Box>
                  <Box component="td" />
                  {CONVERSATION_ENGINES.map((eng) => (
                    <Box component="td" key={eng} sx={{ textAlign: 'center', p: '6px 8px', fontFamily: 'monospace' }}>
                      {state.cells.reduce((s, c) => s + c.engineRuns[eng], 0)}
                    </Box>
                  ))}
                  <Box component="td" sx={{ textAlign: 'center', p: '6px 8px', fontWeight: 700 }}>
                    {totalRuns} <Typography component="span" variant="caption" color="text.secondary">{t('conversations.editorMatrixPerLaunch')}</Typography>
                  </Box>
                </Box>
              </tbody>
            </Box>
          </Box>
        )}

        {/* Établissement zone suggestions — one-click add from the brand's existing locations */}
        {etabZones.length > 0 && (
          <Box sx={{ mb: 1.5 }}>
            <Typography variant="caption" color="text.secondary" fontWeight={700} sx={{ display: 'block', textTransform: 'uppercase', letterSpacing: '0.07em', mb: 0.75 }}>
              {t('conversations.editorZoneSuggestionsLabel')}
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
              {etabZones.slice(0, 5).map((sugg) => {
                const key = zoneKeyFrom(sugg as any);
                const alreadyAdded = state.cells.some((c) => c.zone === key && c.language === zoneLang);
                const cc = sugg.countryCode ?? '';
                const flag = cc ? [...cc.toUpperCase()].map((c) => String.fromCodePoint(0x1f1e6 - 65 + c.charCodeAt(0))).join('') : '';
                return (
                  <Box
                    key={key}
                    component="button"
                    type="button"
                    disabled={alreadyAdded}
                    onClick={() => handleAddZoneFromSuggestion(sugg)}
                    sx={{
                      display: 'inline-flex', alignItems: 'center', gap: 0.625,
                      border: '1px solid', borderColor: alreadyAdded ? 'success.light' : 'divider',
                      bgcolor: alreadyAdded ? 'success.light' : 'background.paper',
                      color: alreadyAdded ? 'success.dark' : 'text.primary',
                      borderRadius: '999px', px: 1.25, py: 0.5,
                      fontSize: '0.8125rem', fontWeight: 500, cursor: alreadyAdded ? 'default' : 'pointer',
                      font: 'inherit',
                      '&:not(:disabled):hover': { borderColor: 'primary.main', bgcolor: 'primary.50' },
                      transition: 'border-color 0.15s, background-color 0.15s',
                    }}
                  >
                    {flag && <span style={{ fontSize: '1em', lineHeight: 1 }}>{flag}</span>}
                    <span>{sugg.label}</span>
                    {alreadyAdded ? <span style={{ fontSize: '0.75em' }}>✓</span> : <span style={{ opacity: 0.5, fontSize: '0.85em' }}>+</span>}
                  </Box>
                );
              })}
            </Box>
          </Box>
        )}

        {/* Zone search */}
        <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, p: 1.5 }}>
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center', mb: 1 }}>
            <Box sx={{ flex: 1, minWidth: 220 }}>
              <ZoneAutocomplete
                query={zoneQuery}
                selected={zoneSuggestionSelected}
                onQueryChange={(v) => setZoneQuery(v)}
                onSelect={(s) => { setZoneSuggestionSelected(s); setZoneQuery(s.label); }}
                onEnter={handleAddZone}
                placeholder={t('conversations.editorMatrixAddZonePlaceholder')}
                lang={zoneLang}
              />
            </Box>
            <FormControl size="small" sx={{ minWidth: 130 }}>
              <InputLabel>{t('conversations.editorMatrixAddZoneLang')}</InputLabel>
              <Select
                label={t('conversations.editorMatrixAddZoneLang')}
                value={zoneLang}
                onChange={(e) => setZoneLang(e.target.value)}
              >
                {LANG_OPTIONS.map((l) => (
                  <MenuItem key={l.value} value={l.value}>{l.flag} {l.label}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <Button
              variant="outlined"
              size="small"
              disabled={!zoneSuggestionSelected}
              onClick={handleAddZone}
            >
              {t('conversations.editorMatrixAddZoneBtn')}
            </Button>
          </Box>
        </Box>

        {/* Quota bars */}
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'center', fontSize: '0.75rem', color: 'text.secondary' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant="caption">{t('conversations.editorMatrixQuotaConfigs', { n: totalConfigs, max: CONVERSATION_MAX_CELLS })}</Typography>
            <LinearProgress
              variant="determinate"
              value={Math.min(100, (totalConfigs / CONVERSATION_MAX_CELLS) * 100)}
              sx={{ width: 80, borderRadius: 4 }}
            />
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant="caption" color={cost.overCapRuns ? 'error' : 'text.secondary'}>
              {t('conversations.editorMatrixQuotaRuns', { n: totalRuns, max: CONVERSATION_MAX_RUNS })}
            </Typography>
            <LinearProgress
              variant="determinate"
              value={Math.min(100, (totalRuns / CONVERSATION_MAX_RUNS) * 100)}
              color={cost.overCapRuns ? 'error' : 'primary'}
              sx={{ width: 80, borderRadius: 4 }}
            />
          </Box>
          {cost.overCapRuns && (
            <Typography variant="caption" color="error" fontWeight={600}>
              {t('conversations.editorErrorQuotaRuns')}
            </Typography>
          )}
        </Box>

        <Typography variant="caption" color="text.secondary" sx={{ fontStyle: 'italic' }}>
          {t('conversations.editorMatrixNoAiOverview')}
        </Typography>

        {/* Cadence + fuseau + heure */}
        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: 1.5, pt: 1 }}>
          <FormControl size="small">
            <InputLabel>{t('conversations.editorCadenceLabel')}</InputLabel>
            <Select
              label={t('conversations.editorCadenceLabel')}
              value={state.cadence}
              onChange={(e) => update('cadence', e.target.value as CadenceKey)}
            >
              <MenuItem value="none">Aucune (manuel uniquement)</MenuItem>
              {CADENCES.filter(c => c !== 'none').map((c) => (
                <MenuItem key={c} value={c}>{t(`conversations.cadence${c.charAt(0).toUpperCase() + c.slice(1)}`)}</MenuItem>
              ))}
            </Select>
          </FormControl>
          {state.cadence !== 'none' && (
            <>
              <FormControl size="small">
                <InputLabel>Fuseau horaire</InputLabel>
                <Select
                  label="Fuseau horaire"
                  value={state.timezone}
                  onChange={(e) => update('timezone', e.target.value)}
                >
                  {TIMEZONE_OPTIONS.map((tz) => (
                    <MenuItem key={tz.value} value={tz.value} sx={{ fontSize: '0.8125rem' }}>{tz.label}</MenuItem>
                  ))}
                </Select>
              </FormControl>
              <TextField
                label={t('conversations.editorHourLabel')}
                size="small"
                value={state.hour}
                onChange={(e) => update('hour', e.target.value)}
                placeholder="06:00"
                inputProps={{ pattern: '[0-2][0-9]:[0-5][0-9]' }}
              />
            </>
          )}
        </Box>
      </Stack>
    );
  };

  // ---------------------------------------------------------------------------
  // Section summary builder
  // ---------------------------------------------------------------------------

  const sectionSummary = (n: number): string => {
    // Tone is no longer unconditional (it is an optional trait now), so reading it here
    // would print « Neutre » forever on a card that never set one. The trait COUNT is
    // the honest summary of what else the user filled in.
    if (n === 1) {
      const parts = [state.name || t('conversations.editorSummarySansNom')];
      if (state.traits.size > 0) parts.push(t('conversations.editorSummaryTraits', { count: state.traits.size }));
      parts.push(`${t('conversations.editorSummaryDecideSur')} ${(state.decisionCriteria || '—').slice(0, 50)}`);
      return parts.join(' · ');
    }
    if (n === 2) return `${CONVERSATION_INTENTS[state.intentId].name} · ${state.startingPrompt ? t('conversations.editorSummaryStartingPromptWritten') : t('conversations.editorSummaryStartingPromptImprovised')}`;
    if (n === 4) {
      const stop = state.mode === 'scripted'
        ? t('conversations.editorSummaryModeScripted')
        : (state.stopCriterion
            ? t(`conversations.editorStop.${state.stopCriterion}`)
            : t('conversations.editorSummaryStopDefault'));
      return `${stop} · ${t('conversations.editorSummaryToursMax', { n: state.maxTurns })}`;
    }
    const cadenceLabel = state.cadence === 'none' ? t('conversations.editorSummaryManuel') : state.cadence;
    return `${state.cells.length > 0 ? t('conversations.editorSummaryZones', { n: state.cells.length }) : t('conversations.editorSummaryNoZone')} · ${cadenceLabel}`;
  };

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------

  /**
   * Section 3 is gone from the accordion — « Qu'est-ce qu'on mesure ? » is the
   * always-visible scope strip rendered after section 2. The `n` values stay 1/2/4/5 so
   * every `editorSec<N>*` i18n key, `openSections` entry and `doneSections` entry keeps
   * its meaning; only the DISPLAYED number is positional.
   */
  const sections = [
    { n: 1, title: t('conversations.editorSec1Title'), q: t('conversations.editorSec1Q'), renderBody: renderSection1 },
    { n: 2, title: t('conversations.editorSec2Title'), q: t('conversations.editorSec2Q'), renderBody: renderSection2 },
    { n: 4, title: t('conversations.editorSec4Title'), q: t('conversations.editorSec4Q'), renderBody: renderSection4 },
    { n: 5, title: t('conversations.editorSec5Title'), q: t('conversations.editorSec5Q'), renderBody: renderSection5 },
  ];

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="xl"
      fullWidth
      PaperProps={{ sx: { maxHeight: '95vh', bgcolor: 'background.default' } }}
    >
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pr: 1 }}>
        <Typography fontWeight={700}>
          {isEdit ? t('conversations.editorTitleEdit') : t('conversations.editorTitleCreate')}
        </Typography>
        <IconButton onClick={onClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent sx={{ p: 0 }}>
        <Box sx={{ display: 'grid', gridTemplateColumns: { md: '1fr 340px' }, gap: 0, height: '100%' }}>
          {/* Left: form */}
          <Box sx={{ overflowY: 'auto', p: 2, display: 'flex', flexDirection: 'column', gap: 1 }}>
            {error && (
              <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 1 }}>
                {error}
              </Alert>
            )}

            {sections.map(({ n, title, q, renderBody }, i) => {
              const isOpen = state.openSections.has(n);
              const isDone = state.doneSections.has(n);
              const isLast = i === sections.length - 1;
              return (
                <Fragment key={n}>
                  <Box
                    sx={{
                      border: '1px solid',
                      borderColor: isOpen ? 'primary.light' : 'divider',
                      borderRadius: 2,
                      bgcolor: 'background.paper',
                      overflow: 'hidden',
                      ...(isOpen ? { outline: '1px solid', outlineColor: 'rgba(124,58,237,0.25)' } : {}),
                    }}
                  >
                    <SectionHeader
                      num={i + 1}
                      title={title}
                      summary={isOpen ? q : sectionSummary(n)}
                      open={isOpen}
                      done={isDone}
                      onToggle={() => toggleSection(n)}
                    />
                    {isOpen && (
                      <>
                        <Divider />
                        <Box sx={{ p: 2.5 }}>
                          {renderBody()}
                          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
                            <Button variant="outlined" size="small" onClick={() => advanceSection(n)}>
                              {isLast ? t('conversations.editorValidateLast') : t('conversations.editorValidate')}
                            </Button>
                          </Box>
                        </Box>
                      </>
                    )}
                  </Box>
                </Fragment>
              );
            })}

            {/* Sticky cost bar */}
            <Box
              sx={{
                position: 'sticky',
                bottom: 0,
                zIndex: 4,
                bgcolor: 'background.paper',
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 2,
                boxShadow: '0 -4px 20px rgba(0,0,0,0.08)',
                mt: 1,
                px: 2,
                py: 1.5,
                display: 'flex',
                flexWrap: 'wrap',
                gap: 1.5,
                alignItems: 'center',
              }}
            >
              <Typography variant="caption" sx={{ flex: 1, minWidth: 0 }}>
                {cost.totalRuns > 0 && (
                  cost.isScripted
                    ? t('conversations.editorCostBarScripted', {
                        runs: cost.totalRuns,
                        turns: cost.turnsCount,
                        credits: cost.creditsPerLaunch.toLocaleString('fr-FR'),
                        monthly: cost.creditsPerMonth.toLocaleString('fr-FR'),
                      })
                    : t('conversations.editorCostBar', {
                        runs: cost.totalRuns,
                        turns: cost.turnsCount,
                        credits: cost.creditsPerLaunch.toLocaleString('fr-FR'),
                        monthly: cost.creditsPerMonth.toLocaleString('fr-FR'),
                      })
                )}
              </Typography>
              <Button variant="outlined" size="small" onClick={onClose} disabled={saving}>
                {t('conversations.editorCancel')}
              </Button>
              <Button
                variant="outlined"
                size="small"
                disabled={!canSubmit || saving}
                onClick={() => handleSubmit(false)}
                startIcon={saving ? <CircularProgress size={14} color="inherit" /> : undefined}
              >
                {t('conversations.editorSave')}
              </Button>
              <Button
                variant="contained"
                size="small"
                disabled={!canSubmit || saving}
                onClick={() => handleSubmit(true)}
                startIcon={saving ? <CircularProgress size={14} color="inherit" /> : undefined}
              >
                {t('conversations.editorSaveAndLaunch')}
              </Button>
            </Box>
          </Box>

          {/* Right: identity recap + metadata (tags, « On mesure »).
              No longer `xs: 'none'` — this column stopped being a read-only preview
              when the tag input and the Cibles CTA moved into it, and hiding an input
              on narrow screens makes the field unreachable. On xs the grid is one
              column, so it stacks under the steps. */}
          <Box
            sx={{
              overflowY: 'auto',
              p: 2,
              bgcolor: 'background.default',
              borderLeft: { md: '1px solid' },
              borderTop: { xs: '1px solid', md: 'none' },
              borderColor: 'divider',
            }}
          >
            <PreviewPanel state={state}>
              {renderTagsCard()}
              {renderScopeStrip()}
            </PreviewPanel>
          </Box>
        </Box>
      </DialogContent>
    </Dialog>
  );
}
